import * as React from 'react';
import { cx } from 'linaria';

import * as Polymorphic from '..';
import { useI18n } from '../i18n-provider';
import { mergeRefs } from '../utils/merge-refs';

import * as styles from './typography.styles';

export interface TypographyOwnProps {
    /**
     *  Text variant style
     *  @default body-1
     */
    variant?:
        | 'heading-1'
        | 'heading-2'
        | 'heading-3'
        | 'heading-4'
        | 'heading-5'
        | 'heading-6'
        | 'body-1'
        | 'body-2'
        | 'label-1'
        | 'label-2'
        | 'caption-1'
        | 'caption-2'
        | 'button';
    /**
     *  Text alignment (same as CSS property text-align).
     *  By default, this is inherited from parent
     */
    align?: 'start' | 'center' | 'end';
    /**
     * Color of the Text
     *  @default body
     */
    color?:
        | 'alert'
        | 'success'
        | 'information'
        | 'warning'
        | 'catapult-orange'
        | 'catapult-black'
        | 'catapult-white'
        | 'body'
        | 'secondary'
        | 'tertiary'
        | 'inherit';
    /**
     *  Truncate multiline Text
     */
    numberOfLines?: number;
    /**
     *  Truncate multiline Text to a single line and allow collapse behaviour
     */
    expandable?: boolean;
    /**
     *  Test id for unit and e2e
     */
    testId?: string;
}

const DEFAULT_TAG = 'div';

export type TypographyComponent = Polymorphic.ForwardRefComponent<typeof DEFAULT_TAG, TypographyOwnProps>;

export const Typography: TypographyComponent = React.forwardRef((props, ref) => {
    const {
        as: As,
        variant = 'body-1',
        color = 'body',
        numberOfLines,
        expandable = false,
        align,
        children,
        testId,
        ...passThroughProps
    } = props;
    const messages = useI18n();

    const tag = React.useMemo(() => getVariantTag(variant), [variant]);
    const Component = As ?? tag;

    const elementRef = React.useRef(null);
    const [isTruncated, isExpanded, setIsExpanded] = useTextExpandable(elementRef, expandable);

    const hasLineClamp = numberOfLines > 0;
    const lineClampStyle = hasLineClamp
        ? ({ [styles.lineClampVar]: isExpanded ? 'unset' : numberOfLines } as React.CSSProperties)
        : {};

    const className = cx(
        !expandable && props.className,
        styles.base,
        styles.variants[variant],
        styles.colors[color],
        align && styles.align[align],
        hasLineClamp && styles.lineClamp,
    );

    const content = (
        <Component
            {...passThroughProps}
            ref={mergeRefs(ref, elementRef)}
            data-testid={testId}
            className={className}
            style={{
                ...passThroughProps.style,
                ...lineClampStyle,
            }}
        >
            {children}
        </Component>
    );

    if (!expandable) {
        return content;
    }

    const wrapperClassName = cx(isTruncated && styles.expandable, props.className);

    return (
        <div className={wrapperClassName} style={lineClampStyle}>
            {content}
            {isTruncated && (
                <button
                    className={cx(styles.expandableButton, styles.base, styles.variants[variant])}
                    type="button"
                    onClick={() => setIsExpanded((state) => !state)}
                >
                    {messages['text.more']}
                </button>
            )}
        </div>
    );
});

Typography.displayName = 'Typography';

// Helper

function getVariantTag(variant: TypographyOwnProps['variant']): React.ElementType {
    switch (variant) {
        case 'heading-1': {
            return 'h1';
        }

        case 'heading-2': {
            return 'h2';
        }

        case 'heading-3': {
            return 'h3';
        }

        case 'heading-4': {
            return 'h4';
        }

        case 'heading-5': {
            return 'h5';
        }

        case 'heading-6': {
            return 'h6';
        }

        case 'label-1':
        case 'label-2': {
            return 'label';
        }

        case 'caption-1':
        case 'caption-2':
        case 'button':
        case 'body-1':
        case 'body-2':
        default: {
            return DEFAULT_TAG;
        }
    }
}

// Expandable

const useTextExpandable = (elementRef: React.RefObject<HTMLElement>, expandable: boolean) => {
    const [isTruncated, setIsTruncated] = React.useState(false);
    const [isExpanded, setIsExpanded] = React.useState(false);

    React.useEffect(() => {
        if (!expandable || !elementRef.current) {
            return;
        }

        const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
            for (const entry of entries) {
                const shouldTruncate = entry.target.scrollHeight > (entry.target as HTMLElement).offsetHeight;
                setIsTruncated(shouldTruncate);
            }
        });

        resizeObserver.observe(elementRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, [expandable, elementRef]);

    return [isTruncated, isExpanded, setIsExpanded] as const;
};
